import React from "react";
import { connect } from "react-redux";
import posthog from "posthog-js";

import { withTranslation } from "react-i18next";

import { authActions, passwordActions } from "../../_actions";

import { Link } from "react-router-dom";
import { PASSWORD, routesConstants as ROUTES } from "../../_constants";

import { validators } from "../../_utils";
import { ERROR_TYPES, classifyError, getErrorMessageKey, getLegacyFallbackMessageKey, getOtpResendCooldownSeconds, isOtpSendThrottled, shouldShowPasswordRecovery, shouldShowRemainingAttempts } from "../../_utils/errorClassifier";

import { classListService, settingsService } from "../../_services";

import FlowLink from "../../_components/FlowLink";
import Turnstile from "../../_components/Turnstile";

import IconEye from "../../_components/svg/EyeOpened.jsx";
import IconEyeActive from "../../_components/svg/EyeOpenedActive.jsx";
import { oauthActions } from "../../_actions/oauth.actions.js";

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

class AuthFormLogin extends React.Component {
    constructor(props) {
        super(props);

        this.settings = settingsService.get();
        this.COMPANY = this.settings.COMPANY;
        this.IS_COMPANY_LOGIN = this.settings.companyLogin;
        // Company login uses /company-login (email+password) and does not support AWA
        this.flow = this.IS_COMPANY_LOGIN ? "legacy" : props.flow;

        this.props = props;
        this.state = {
            email: this.props.password.email,
            password: "",
            otpCode: "",
            awaStep: "email",
            passwordType: "password",
            error: "",
            errorType: null,
            erroremail: false,
            submitted: false,
            emailLocked: false,
            processingOtp: false,
            success: false,
            rememberMe: false,
            turnstileToken: "",
            turnstileResetTrigger: null,
            resendCountdown: 0,
        };

        this.resendTimer = null;

        this.canBeSubmitted = this.canBeSubmitted.bind(this);
        this.validStateValue = this.validStateValue.bind(this);
        this.togglePasswordType = this.togglePasswordType.bind(this);
        this.handleInput = this.handleInput.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleRecover = this.handleRecover.bind(this);
        this.handleTurnstileVerify = this.handleTurnstileVerify.bind(this);
        this.handleTurnstileError = this.handleTurnstileError.bind(this);
        this.handleTurnstileExpire = this.handleTurnstileExpire.bind(this);
        this.handleResendOtp = this.handleResendOtp.bind(this);
        this.handleChangeEmail = this.handleChangeEmail.bind(this);
        this.handleUseLegacy = this.handleUseLegacy.bind(this);
        this.startResendCountdown = this.startResendCountdown.bind(this);
        this.clearResendTimer = this.clearResendTimer.bind(this);
    }

    componentWillUnmount() {
        this.clearResendTimer();
    }

    clearResendTimer() {
        if (this.resendTimer) {
            clearTimeout(this.resendTimer);
            this.resendTimer = null;
        }
    }

    startResendCountdown(seconds = 60) {
        this.clearResendTimer();
        this.setState({ resendCountdown: seconds });

        const tick = () => {
            this.setState(
                (previousState) => ({
                    resendCountdown: Math.max(previousState.resendCountdown - 1, 0),
                }),
                () => {
                    if (this.state.resendCountdown > 0) {
                        this.resendTimer = setTimeout(tick, 1000);
                    } else {
                        this.clearResendTimer();
                    }
                }
            );
        };

        this.resendTimer = setTimeout(tick, 1000);
    }

    formatResendCountdown(totalSeconds) {
        if (totalSeconds >= 60) {
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `${minutes}:${String(seconds).padStart(2, "0")}`;
        }
        return `${totalSeconds}s`;
    }


    componentWillReceiveProps(nextProps) {
        // Each OTP-required response (initial send, resend, or a throttled
        // send that still returns nextStep=otp) bumps otpStepNonce so we can
        // react even when the user is already on the OTP step.
        const otpRequested =
            nextProps.auth.awaitingOtp &&
            nextProps.auth.otpStepNonce !== this.props.auth.otpStepNonce;

        if (otpRequested) {
            const reason = nextProps.auth.awaitingOtpReason;
            const errorType = reason ? classifyError(reason) : null;
            const throttled = isOtpSendThrottled(errorType);

            this.setState({
                awaStep: "otp",
                submitted: false,
                processingOtp: false,
                turnstileToken: "",
                turnstileResetTrigger: Date.now(),
                error: throttled ? reason : "",
                errorType: throttled ? errorType : null,
            });
            // COOLDOWN -> 60s, RATE_LIMITED -> 15min, normal send -> 60s.
            this.startResendCountdown(getOtpResendCooldownSeconds(errorType));
            classListService.remove();
            return;
        }

        if (nextProps.auth.awaitingPassword && !this.props.auth.awaitingPassword) {
            const reason = nextProps.auth.awaitingPasswordReason;
            this.setState({
                awaStep: "legacy",
                submitted: false,
                turnstileToken: "",
                turnstileResetTrigger: Date.now(),
                error: reason || "",
                errorType: reason ? classifyError(reason) : null,
            });
            classListService.remove();
            return;
        }

        // Case of error
        if (nextProps.auth.hasOwnProperty("error")) {
            const errorType = classifyError(nextProps.auth.error);

            if (this.state.awaStep === "otp" && errorType === ERROR_TYPES.AUTHENTICATION) {
                this.setState({ awaStep: "legacy", error: "", errorType: null, submitted: false, turnstileToken: "", turnstileResetTrigger: Date.now() });
                classListService.remove();
                return;
            }

            this.setState((previousState) => ({
                ...previousState,
                error: nextProps.auth.error,
                errorType: errorType,
                submitted: false,
                turnstileToken: "",
                processingOtp: false,
                turnstileResetTrigger: Date.now(),
                ...(errorType === ERROR_TYPES.OTP_INVALID && { otpCode: "" }),
            }));

            classListService.remove();
        }

        if (
            nextProps.oauth.isActive &&
            nextProps.oauth.hasOwnProperty("error")
        ) {
            const errorType = classifyError(nextProps.oauth.error);
            this.setState((previousState) => ({
                ...previousState,
                error: nextProps.oauth.error,
                errorType: errorType,
                submitted: false,
                turnstileToken: "",
                turnstileResetTrigger: Date.now(),
            }));

            classListService.remove();
        }
    }

    canBeSubmitted() {
        let { email, password, otpCode, awaStep, erroremail, turnstileToken } = this.state;
        const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        const isCaptchaEnabled = posthog.isFeatureEnabled('enable-auth-captcha');
        const isAwa = this.flow === 'awa';

        if (isAwa && awaStep === 'otp') {
            const base = email && !erroremail && otpCode;
            if (turnstileSiteKey && isCaptchaEnabled) return base && turnstileToken;
            return base;
        }

        if (isAwa && awaStep === 'legacy') {
            const base = email && !erroremail && password;
            if (turnstileSiteKey && isCaptchaEnabled) return base && turnstileToken;
            return base;
        }

        // Only require turnstile token if site key is configured AND feature flag is enabled
        if (turnstileSiteKey && isCaptchaEnabled) {
            return email && !erroremail && (isAwa || password) && turnstileToken;
        }

        return email && !erroremail && (isAwa || password);
    }

    validStateValue(key, value) {
        const _value = value || this.state[key];

        if (validators[key]) {
            const isInvalid = !validators[key](_value);

            this.setState((previousState) => ({
                ...previousState,
                ["error" + key]: isInvalid,
            }));

            return isInvalid;
        }

        return _value && _value !== "";
    }

    togglePasswordType() {
        const type = this.state.passwordType;

        this.setState((previousState) => ({
            ...previousState,
            passwordType: type !== "password" ? "password" : "text",
        }));
    }

    handleInput(evt) {
        const name = evt.target.name;
        const value = evt.target.value;

        this.setState(
            {
                [name]: value,
            },
            () => {
                this.validStateValue(name, value);

                if (name === "email" && !value) {
                    const { dispatch } = this.props;

                    dispatch(passwordActions.setEmail(""));
                }
            }
        );
    }

    handleSubmit(evt) {
        evt.preventDefault();
        posthog.capture('auth:auth_login_click')

        if (this.state.submitted || !this.canBeSubmitted()) {
            return;
        }

        classListService.add();

        this.setState((previousState) => ({
            ...previousState,
            submitted: true,
            emailLocked: true,
            passwordType: "password",
        }));
        const { dispatch, trusted } = this.props;
        const { hash } = this.props.password;
        const { email, password, otpCode, awaStep, rememberMe, turnstileToken } = this.state;
        const { device } = trusted || {};
        const isPasswordReset = hash === PASSWORD.TRACK;
        const isOauthActive = this.props.oauth.isActive;
        const isAwa = this.flow === 'awa';

        dispatch(passwordActions.setHash(""));

        if (isAwa && awaStep === 'otp') {
            this.setState({processingOtp: true});
            dispatch(authActions.login(email, null, device, false, turnstileToken, true, {}, otpCode));
            return;
        }

        if (isAwa && awaStep === 'legacy') {
            dispatch(authActions.login(email, password, device, rememberMe, turnstileToken, false, { isPasswordReset }));
            return;
        }

        if (isOauthActive) {
            dispatch(
                oauthActions.login(email, password, this.props.oauth.flowId, turnstileToken)
            );
            return;
        }
        dispatch(
            authActions.login(email, password, device, rememberMe, turnstileToken, isAwa, { isPasswordReset })
        )
    }

    handleRecover() {
        const { dispatch } = this.props;
        const { email, invalidEmail } = this.state;
        posthog.capture('auth:auth_forgot_password_click');
        classListService.add();
        classListService.remove(500);

        if (!email || invalidEmail) {
            return;
        }

        dispatch(passwordActions.setEmail(email));
    }

    handleTurnstileVerify(token) {
        this.setState({
            turnstileToken: token,
        });
    }

    handleTurnstileError() {
        this.setState({
            turnstileToken: "",
        });
    }

    handleTurnstileExpire() {
        this.setState({
            turnstileToken: "",
        });
    }

    handleResendOtp() {
        const { dispatch, trusted } = this.props;
        const { email, turnstileToken } = this.state;
        const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        const isCaptchaEnabled = posthog.isFeatureEnabled('enable-auth-captcha');
        if (turnstileSiteKey && isCaptchaEnabled && !turnstileToken) return;
        const { device } = trusted || {};
        this.setState({ otpCode: "", error: "", errorType: null, submitted: true, processingOtp: true });
        this.startResendCountdown();
        classListService.add();
        dispatch(authActions.login(email, "", device, false, turnstileToken, true, {}));
    }

    handleChangeEmail() {
        this.setState({
            awaStep: "email",
            otpCode: "",
            password: "",
            error: "",
            errorType: null,
            submitted: false,
            emailLocked: false,
            processingOtp: false,
            turnstileToken: "",
            turnstileResetTrigger: Date.now(),
        });
    }

    handleUseLegacy() {
        this.clearResendTimer();
        this.setState({
            awaStep: "legacy",
            otpCode: "",
            password: "",
            error: "",
            errorType: null,
            submitted: false,
            processingOtp: false,
            turnstileToken: "",
            turnstileResetTrigger: Date.now(),
            resendCountdown: 0,
        });
    }

    render() {
        const canSubmit = this.canBeSubmitted();
        const isBackoffice = window.location.href.includes("backoffice");
        const { isFromIngresseDomain } = this.props.auth;
        const isAwa = this.flow === 'awa';
        const { awaStep } = this.state;
        const isCaptchaEnabled = posthog.isFeatureEnabled('enable-auth-captcha');
        const shouldShowTurnstile = process.env.REACT_APP_TURNSTILE_SITE_KEY && isCaptchaEnabled;

        const attempts = sessionStorage.getItem("remainingAttempts") !== null ? sessionStorage.getItem("remainingAttempts") : 3;
        const hasAnyError = !!this.state.error;
        const isOtpFallback = awaStep === 'legacy' && [
            ERROR_TYPES.OTP_EXPIRED,
            ERROR_TYPES.OTP_LOCKED,
            ERROR_TYPES.OTP_INVALID,
            ERROR_TYPES.OTP_SEND_COOLDOWN,
            ERROR_TYPES.OTP_SEND_RATE_LIMITED
        ].includes(this.state.errorType);
        const hasOtpLocked = hasAnyError && (this.state.errorType === ERROR_TYPES.OTP_EXPIRED || this.state.errorType === ERROR_TYPES.OTP_LOCKED || this.state.errorType === ERROR_TYPES.OTP_SEND_RATE_LIMITED);
        const showOTPInput = isAwa && awaStep === 'otp' && !this.state.processingOtp && !hasOtpLocked;

        return (
            <form
                className="aph form"
                noValidate={true}
                onSubmit={this.handleSubmit}
            >
                <div className="aph m-10-bot">
                    <label className="aph form__label" htmlFor="email">
                        {isBackoffice
                            ? "E-MAIL"
                            : this.props.t("AUTH.LABELS.EMAIL")}
                    </label>
                    <input
                        className={`aph form__control ${
                            !isFromIngresseDomain && "custom_mrt_input"
                        }`}
                        type="email"
                        id="email"
                        name="email"
                        required={true}
                        disabled={!!this.state.emailLocked}
                        value={this.state.email}
                        onChange={this.handleInput}
                    />
                    {this.state.erroremail && (
                        <div className="wrapper-wrong-email">
                            <span>
                                {this.props.t("VALIDATIONS.EMAIL")}
                            </span>
                        </div>
                    )}
                </div>
                {isAwa && awaStep === 'email' && this.state.error && (
                    <div className="aph form__helper text-red wrapper-attempts">
                        <div className="wrapper-msg-alert">
                            <strong>
                                {this.props.t(getErrorMessageKey(this.state.errorType))}
                                {shouldShowRemainingAttempts(this.state.errorType) && (
                                    <span className="msg-attempts">
                                        {this.props.t("VALIDATIONS.ATTEMPTS_LEFT").replace('<@attempts>', attempts)}
                                    </span>
                                )}
                            </strong>
                        </div>
                        {shouldShowPasswordRecovery(this.state.errorType) && (
                            <div className="wrapper-msg-info">
                                <span className="msg-info-text">
                                    {this.props.t("VALIDATIONS.AFTER_3_ATTEMPTS_RESET_PASSWORD")}
                                    <Link to="/password/recover" className="recovery-link">
                                        {this.props.t("VALIDATIONS.CLICK_HERE")}
                                    </Link>
                                    <span>
                                        {this.props.t("VALIDATIONS.TO_CHANGE_IT_NOW")}
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {isAwa && awaStep === 'otp' && (
                <div className="aph m-10-top m-20-bot">
                    {showOTPInput ? (
                        <>
                        <label className="aph form__label" htmlFor="otpCode">
                            {this.props.t("AUTH.LABELS.OTP_CODE")}
                        </label>
                        <input
                            className={`aph form__control ${!isFromIngresseDomain && "custom_mrt_input"}`}
                            type="text"
                            id="otpCode"
                            name="otpCode"
                            required
                            autoFocus
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            onChange={this.handleInput}
                            value={this.state.otpCode}
                        />
                        <div className="aph form__helper auth-otp-sent-info">
                            <span>{this.props.t("AUTH.LOGIN.OTP_SENT_INFO")}</span>
                        </div>
                        </>
                    ) : (hasOtpLocked ? (<></>) : (
                        <div className="auth-otp-spinner-wrapper">
                            <div className="auth-otp-spinner" />
                        </div>
                    ))}
                    {hasAnyError && (
                        <div className="aph form__helper text-red wrapper-attempts">
                            <div className="wrapper-msg-alert">
                                <strong>{this.props.t(getErrorMessageKey(this.state.errorType))}</strong>
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="aph m-10-top auth-otp-actions">
                            <button
                                type="button"
                                onClick={this.handleChangeEmail}
                                className="auth-otp-link-btn"
                            >
                                {this.props.t("AUTH.LOGIN.OTP_CHANGE_EMAIL")}
                            </button>
                            <button
                                type="button"
                                onClick={this.handleResendOtp}
                                className="auth-otp-link-btn"
                                disabled={!!(shouldShowTurnstile && !this.state.turnstileToken) || this.state.resendCountdown > 0}
                            >
                                {this.state.resendCountdown > 0
                                    ? this.props.t("AUTH.LOGIN.OTP_RESEND_IN", { time: this.formatResendCountdown(this.state.resendCountdown) })
                                    : this.props.t("AUTH.LOGIN.OTP_RESEND")}
                            </button>
                        </div>
                    </div>
                </div>
                )}
                {(!isAwa || awaStep === 'legacy') && (
                <div className="aph m-10-top m-20-bot">
                    <label className="aph form__label" htmlFor="password">
                        {isBackoffice
                            ? "SENHA"
                            : this.props.t("AUTH.LABELS.PASSWORD")}
                    </label>
                    <div className="aph form__control-with-icon">
                        <input
                            className={`aph form__control ${
                                !isFromIngresseDomain && "custom_mrt_input"
                            }`}
                            type={this.state.passwordType}
                            id="password"
                            name="password"
                            required={true}
                            disabled={!!this.state.submitted}
                            value={this.state.password}
                            onChange={this.handleInput}
                        />
                        <button
                            className={`aph form__control-with-icon__button`}
                            type="button"
                            id="btnPasswordToggle"
                            disabled={!!this.state.submitted}
                            onClick={this.togglePasswordType}
                            >
                            {this.state.passwordType === "password" ? (
                                <IconEye />
                            ) : (
                                <IconEyeActive />
                            )}
                        </button>
                    </div>
                            {(this.state.error) && (
                            <div className="aph form__helper text-red wrapper-attempts">
                                <div className="wrapper-msg-alert">
                                    <strong>
                                        {!isOtpFallback && this.props.t(getErrorMessageKey(this.state.errorType))}
                                        {shouldShowRemainingAttempts(this.state.errorType) && (
                                            <span className="msg-attempts">
                                                {this.props.t("VALIDATIONS.ATTEMPTS_LEFT").replace('<@attempts>', attempts)}
                                            </span>
                                        )}
                                    </strong>
                                </div>
                                {shouldShowPasswordRecovery(this.state.errorType) && (
                                    <div className="wrapper-msg-info">
                                        <span className="msg-info-text">
                                        {this.props.t("VALIDATIONS.AFTER_3_ATTEMPTS_RESET_PASSWORD")}
                                            <Link to="/password/recover" className="recovery-link">
                                            {this.props.t("VALIDATIONS.CLICK_HERE")}
                                            </Link>
                                            <span>
                                            {this.props.t("VALIDATIONS.TO_CHANGE_IT_NOW")}
                                            </span>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    {!this.IS_COMPANY_LOGIN && !this.state.error && (
                        <div className="aph form__helper">
                            <div className="text-right text-bold">
                                <FlowLink
                                    className={`aph ${
                                        !isFromIngresseDomain &&
                                        "custom_mrt_text_link"
                                    }`}
                                    to={ROUTES.PASSWORD.RECOVER}
                                    title={this.props.t(
                                        "AUTH.LOGIN.RECOVER.TITLE"
                                    )}
                                    onClick={this.handleRecover}
                                    id="linkPasswordRecover"
                                >
                                    {this.props.t("AUTH.LOGIN.RECOVER.TEXT")}
                                </FlowLink>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {shouldShowTurnstile && (
                    <div className="aph m-20-ver">
                        <Turnstile
                            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                            onVerify={this.handleTurnstileVerify}
                            onError={this.handleTurnstileError}
                            onExpire={this.handleTurnstileExpire}
                            resetTrigger={this.state.turnstileResetTrigger}
                            className="turnstile-login"
                        />
                    </div>
                )}

                <div className="aph m-30-ver show-xs" />
                {!(isAwa && awaStep === 'otp' && this.state.submitted) && (
                <div
                    className={`aph m-20-too ${
                        this.IS_COMPANY_LOGIN ? "p-10-ver" : ""
                    }`}
                >
                    <button
                        className={`aph btn btn--block btn--primary ${
                            !isFromIngresseDomain && "custom_mrt_button_submit"
                        }`}
                        type="submit"
                        id="btnLoginSubmit"
                        disabled={!!(this.state.submitted || !canSubmit || (this.state.error && attempts <= 0))}
                    >
                        {isAwa && awaStep === 'email'
                            ? this.props.t("AUTH.LOGIN.CONTINUE")
                            : isAwa && awaStep === 'otp'
                                ? this.props.t("AUTH.LOGIN.OTP_CONFIRM")
                                : this.props.t("AUTH.LOGIN.SUBMIT")}
                    </button>
                </div>
                )}
                {isAwa && awaStep === 'otp' && !this.state.submitted && (
                    <div className="aph m-15-top">
                        <button
                            type="button"
                            onClick={this.handleUseLegacy}
                            className={`aph btn btn--block btn--bordered btn--primary ${
                                !isFromIngresseDomain && "custom_mrt_button_register"
                            }`}
                            id="btnLoginUsePassword"
                        >
                            {this.props.t("AUTH.LOGIN.OTP_USE_PASSWORD")}
                        </button>
                    </div>
                )}
                {!this.IS_COMPANY_LOGIN && (
                    <div className="aph m-15-top p-10-bot">
                        <FlowLink
                            onClick={() => {posthog.capture('auth:auth_create_account_click')}}
                            className={`aph btn btn--block btn--bordered btn--primary ${
                                !isFromIngresseDomain &&
                                "custom_mrt_button_register"
                            }`}
                            id="linkLoginToRegister"
                            to={
                                this.props.oauth.flowId
                                    ? ROUTES.OAUTH.REGISTER
                                    : ROUTES.AUTH.REGISTER
                            }
                        >
                            {this.props.t("AUTH.CHOICES.REGISTER")}
                        </FlowLink>
                    </div>
                )}
            </form>
        );
    }
}

function mapStateToProps(state) {
    return state;
}

export default withTranslation()(connect(mapStateToProps)(AuthFormLogin));
